import { startOfMonth, endOfMonth, differenceInCalendarMonths, format } from 'date-fns';
export const snapCheckIn = (date: Date): Date => startOfMonth(date);
export const snapCheckOut = (date: Date): Date => {
  const eom = endOfMonth(date);
  return new Date(eom.getFullYear(), eom.getMonth(), eom.getDate());
};
export const monthsInclusive = (checkIn: Date, checkOut: Date): number =>
  differenceInCalendarMonths(checkOut, checkIn) + 1;

/**
 * Whole calendar months a stay spans when billed monthly, computed from the
 * student's EXACT picked dates. The student may pick any day (e.g. 5 Jul ->
 * 20 Oct); billing snaps the range to whole months (1 Jul -> 31 Oct = 4).
 */
export const monthsInclusiveSnapped = (checkIn: Date, checkOut: Date): number =>
  monthsInclusive(snapCheckIn(checkIn), snapCheckOut(checkOut));

const formatRange = (start: Date, end: Date, n: number): string =>
  `${format(start, 'd MMM yyyy')} → ${format(end, 'd MMM yyyy')} · you pay ${n} month${n > 1 ? 's' : ''} (${format(start, 'MMM')}–${format(end, 'MMM')})`;

/** Hint from already-snapped month-bound dates. */
export const formatStayHint = (checkIn: Date, checkOut: Date): string =>
  formatRange(checkIn, checkOut, monthsInclusive(checkIn, checkOut));

/**
 * Hint from the student's EXACT picked dates. Shows the whole-month billing
 * range (snapped) + month count, so the student understands what they pay even
 * though the pickers display their exact chosen days.
 */
export const formatStayHintFromExact = (checkIn: Date, checkOut: Date): string => {
  const start = snapCheckIn(checkIn);
  const end = snapCheckOut(checkOut);
  return formatRange(start, end, monthsInclusive(start, end));
};

/**
 * First day of the earliest month a student may move out in: with monthly
 * billing, any day in month (checkIn month + minStay - 1) yields exactly
 * `minStay` billed months. E.g. check-in 5 Jul, minStay 4 -> 1 Oct.
 */
export const earliestCheckOutMonth = (checkIn: Date, minStay: number): Date => {
  const start = snapCheckIn(checkIn);
  return new Date(start.getFullYear(), start.getMonth() + (minStay - 1), 1);
};

/**
 * Default move-out to auto-fill: the last day of the minimum-stay month
 * window, i.e. exactly `minStay` billed months. E.g. 5 Jul, minStay 4 -> 31 Oct.
 */
export const defaultCheckOut = (checkIn: Date, minStay: number): Date =>
  snapCheckOut(earliestCheckOutMonth(checkIn, minStay));
